import React from "react";
import { useRecoilValue } from 'recoil';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import { Container, Typography, Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faUserEdit, faCalendarAlt, faBookOpen, faNewspaper } from "@fortawesome/free-solid-svg-icons";

import { userInfo } from "../../auth/AuthProvider";
import { useBoardManageItemQuery } from "../../hooks/useBoard/useBoardManageQuery";
import { useBoardItemQuery, useBoardItemDeleteMutation } from "../../hooks/useBoard/useBoardQuery";
import { isDebug, utils } from "../../utils";

import ContentLayout from "../../layouts/ContentLayout";
import { ControlComment, ControlWriteArticle, ControlSnackbar, ControlCircularProgressBar, Dialog4Confirm, Dialog4Alert, UserType2Value, CheckIsWriter } from "../../components/board";


export default function QnaView() {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useRecoilValue(userInfo);
    const { t } = useTranslation();
    const paramQueryOptions = { ...location.state }
    const { manageid, boardid } = useParams();
    const isLoadingRef = React.useRef(false);
    const refConfirm = React.useRef(null);
    const refAlert = React.useRef(null);
    const refSnackbar = React.useRef(null);
    const showTimerControler = React.useRef(null);

    const [ pageTitle ] = React.useState(manageid === process.env.REACT_APP_BOARD_QNA_MANAGE_ID ? t('nav.board2') : t('nav.board3'));
    const defaultBoardInfo = {ManageId: process.env.REACT_APP_BOARD_QNA_MANAGE_ID, BoardName: null, BoardType: null, NewYn: "N", NewTerm: "7", CommentYn: "N", WriteRole: "system", ReadRole: "system", OrdYn: "N" };
    const [ boardInfo, setBoardInfo] = React.useState(defaultBoardInfo);
    const { error: boardInfoError, data: boardInfoData } = useBoardManageItemQuery(process.env.REACT_APP_BOARD_QNA_MANAGE_ID);

    const defaultPostVlues = {
        BoardId: boardid,
        BoardTitle: "",
        BoardType: "",
        Content: "",
        ReplyBody: "",
        BoardHit: 0,
        ManageId: manageid,
        BoardOrder: 100,
        CreatedDate: new Date().toISOString().slice(0, 10),
        CreatedByName: "",
        CreatedBy: ""
    };
    const [postValue, setPostValue] = React.useState(defaultPostVlues);
    const [openEditDialog, setOpenEditDialog] = React.useState({isOpen: false, boardId: 0});
    const { mutate: deleteBoardItem } = useBoardItemDeleteMutation();
        
    const { error: itemArticleError, data: itemArticleData } = useBoardItemQuery(manageid, boardid);
    const boardType = boardInfo?.BoardType ?? "qna";

    
    const goToList = () => {
        refConfirm.current.showMessageBox(`${t('button.tothelsit')}`, `${t('alert.tothelsit')}`, (result) => {
            if(result) {
                setTimeout(() => {
                    navigate(`/${boardType}`, { replace: false, state: paramQueryOptions });
                }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
            }
        });
    };

    const callRegistration = (e) => saveAction('regist', e);
    const callModify = (e) => saveAction('modify', e);
    const callDelete = (e) => handleAction('delete', e);
    
    const checkPermissions = () => {  
        /// 사용자 읽기권한은 basic이면 모든 사람이 읽을 수 있다.
        if (Boolean(boardInfo?.BoardName) && UserType2Value(boardInfo?.ReadRole ?? 'basic') > 1 && UserType2Value(boardInfo?.ReadRole ?? 'basic') > UserType2Value(auth?.userType ?? 'basic')) { 
            if( isDebug() ) console.log('checkPermissions', new Date().toISOString(), boardInfo?.ReadRole??'basic', auth, auth?.userType ?? 'basic'
                                , UserType2Value(boardInfo?.ReadRole ?? 'basic') > 1
                                , UserType2Value(boardInfo?.ReadRole ?? 'basic'), UserType2Value(auth?.userType ?? 'basic'));

            refAlert.current.showMessageBoxResult(t('error.title'), t('error.notrole'), () => {
                navigate(`/${boardType}`, { replace: false, state: paramQueryOptions });
            });
            return false;
        }
        return true;
    };
    
    const checkPermissions4Save = () => {  
        if (Boolean(boardInfo?.BoardName) && UserType2Value(boardInfo?.WriteRole ?? 'system') > UserType2Value(auth?.userType ?? 'zero')) {
            if( isDebug() ) console.log('checkPermissions4Save', new Date().toISOString(), boardInfo?.WriteRole??'system', auth, auth?.userType ?? 'zero');

            refAlert.current.showMessageBoxResult(t('error.title'), t('error.notrole'), () => {
                navigate(`/${boardType}`, { replace: false, state: paramQueryOptions });
            });
            return;
        }
        return true;
    };

    const saveAction = (actionType, e) => {
        e.preventDefault();
        
        if(auth.userId === undefined || auth.userId === null || auth.userId === '') {
            setTimeout(() => {
                navigate(`/login`);
            }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
        }
        else {
            checkPermissions4Save();

            if (isLoadingRef.current) return;
            isLoadingRef.current = true;
            setOpenEditDialog({isOpen: true, boardId: actionType === 'modify' ? postValue.BoardId : 0});
            isLoadingRef.current = false;
        }
    };

    const handleAction = (actionType, e) => {
        if(auth.userId === undefined || auth.userId === null || auth.userId === '') {
            setTimeout(() => {
                navigate(`/login`);
            }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
        }
        else {
            checkPermissions4Save();
            if( isDebug() ) console.log('handleAction', new Date().toISOString(), auth, auth?.userType ?? 'zero', postValue?.CreatedBy, auth?.userId);
            if(UserType2Value(auth?.userType ?? 'zero') < 7 && postValue?.CreatedBy !== auth.userId) {
                refAlert.current.showMessageBoxResult(t('error.title'), t('error.notrole'), () => { 
                    navigate(`/${boardType}`, { replace: false, state: paramQueryOptions });
                });
                return;
            }

            const actionMessages = {
                modify: { title: t('button.modify'), confirm: t('alert.modify'), success: t('message.modifycomplet') },
                delete: { title: t('button.delete'), confirm: t('alert.delete'), success: t('message.deletecomplet') }
            };

            refConfirm.current.showMessageBox(actionMessages[actionType].title, actionMessages[actionType].confirm, (result) => {
                if(result) {

                    if (isLoadingRef.current)  return;
                    isLoadingRef.current = true;

                    if(actionType === 'delete') {
                        // 삭제하고
                        deleteBoardItem(postValue.BoardId, {
                            onSuccess: () => {
                            refAlert.current.showMessageBox(t('button.delete'), t('message.deletecomplet'));
                            },
                        });
                    }

                    isLoadingRef.current = false;
                    refAlert.current.showMessageBox(actionMessages[actionType].title, actionMessages[actionType].success);
                    setTimeout(() => {
                        navigate(`/${boardType}`, { replace: false, state: paramQueryOptions });
                    }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
                }
            });
        }
    };

    const closeEditDialog = (boolValue) => {
        if(boolValue) {
            // 조회를 다시한다.
        }
        if(openEditDialog.isOpen) setOpenEditDialog({isOpen: false, boardId: 0});
    };

    
    React.useEffect(() => {
        document.title = 'BWOB - ' + pageTitle;
        try {
            if(boardInfoData !== undefined && boardInfoData !== null && boardInfoData.BoardTitle !== "")
            {
                if(boardInfoData.data.BoardType !== "qna")
                {
                    navigate(`/${boardType}`, { replace: false, state: paramQueryOptions });
                }
                setBoardInfo(boardInfoData.data);
            }
        }
        catch(err)
        {
            console.log('Access Deny', new Date().toISOString(), boardType, boardInfoData, err);
        }

        return () => {
            setBoardInfo(defaultBoardInfo);
        } 
    }, [boardInfoError, boardInfoData]);
            
    React.useEffect(() => {
        try {
            if (itemArticleData !== undefined && itemArticleData !== null) {
                setPostValue(itemArticleData);
                checkPermissions();
            }
        }
        catch(err)
        {
            console.log(new Date().toISOString(), boardType, itemArticleData, err);
        }
    }, [itemArticleError, itemArticleData]);


    return (
        <ContentLayout pageTitle={pageTitle} pageDescription={t('nav.board2_description')} addClassName="content-layout-sub2">

            {/* 버튼 영역 */}
            <div style={{ display: 'block', width: '100%', height: '5px' }} />
            <Container className="page-content" sx={{ p: 1 }}>
                <Grid container spacing={1}>
                    <Grid size={{ xs: 0.1, sm: 1, md: 1 }} sx={{ paddingRight: '10px'}} >
                    </Grid>
                    <Grid size={{ xs: 0.1, sm: 1, md: 1 }} sx={{ paddingRight: '10px'}} >
                    </Grid>
                    <Grid size={{ xs: 10.8, sm: 10, md: 10 }} sx={{ textAlign: 'right' }} >
                        <div>
                            <Button variant="outlined" size="medium" startIcon={<ListAltIcon />} onClick={goToList}>
                                {t('button.tothelsit')}
                            </Button>
                            {' '}
                            <Button variant="outlined" size="medium" startIcon={<EditIcon />} onClick={callModify} disabled={!CheckIsWriter(auth, postValue.CreatedBy)} >
                                {t('button.modify')}
                            </Button>
                            {' '}
                            <Button variant="outlined" size="medium" startIcon={<RemoveCircleIcon />} onClick={callDelete} disabled={!CheckIsWriter(auth, postValue.CreatedBy)} >
                                {t('button.delete')}
                            </Button>
                            {' '}
                            <Button variant="outlined" size="medium" startIcon={<AddCircleIcon />} onClick={callRegistration} disabled={!auth.userId}>
                                {t('button.registration')}
                            </Button>
                        </div>
                    </Grid>
                </Grid>
            </Container>

            {/* 본문 내용 */}
            <Container sx={{ p: 1 }}>
                <Grid container alignItems="center" spacing={1} sx={{  '--Grid-borderWidth': '1px',
                                                                        borderTop: 'var(--Grid-borderWidth) dotted',
                                                                        borderBottom: 'var(--Grid-borderWidth) dotted',
                                                                        borderColor: '#403C40',
                                                                        '& > div.divline': {
                                                                            borderTop: 'var(--Grid-borderWidth) dotted',
                                                                            borderColor: 'divider',
                                                                            lineHeight: '3px',
                                                                            height: '3px',
                                                                        },
                                                                        '& > div.divdarkline': {
                                                                            borderTop: 'var(--Grid-borderWidth) dotted',
                                                                            borderColor: '#908790',
                                                                            lineHeight: '3px',
                                                                            height: '3px',
                                                                        },
                }}>
                    <Grid item size={{ xs: 12, sm: 12, md: 12,  }} sx={{ paddingTop: '7px' }}>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1, textAlign: "center", fontWeight: 500 }} color="primary">
                            [{t('field.boardtitle')}] : &nbsp;
                            <FontAwesomeIcon icon={faCircleCheck} style={{ paddingLeft: '0px', paddingRight: '10px' }} />
                             {postValue.BoardTitle}
                        </Typography>
                    </Grid>

                    <Grid item className="divdarkline" size={{ xs: 12, sm: 12, md: 12,  }} />
                    <Grid item size={{ xs: 5, md: 2 }} sx={{ }} >
                        <Typography component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                            <FontAwesomeIcon icon={faUserEdit} style={{ paddingLeft: '0px', paddingRight: '10px' }} />
                            {t('field.createdbyname')}
                        </Typography>
                    </Grid>
                    <Grid item size={{ xs: 7, md: 10 }} sx={{ paddingRight: '10px'}} >
                        <Typography component="div" sx={{ flexGrow: 1, textAlign: "left" }} color="primary">
                            {postValue.CreatedByName} &nbsp;
                        </Typography>
                    </Grid>

                    <Grid item className="divline" size={{ xs: 12, sm: 12, md: 12,  }} />
                    <Grid item size={{ xs: 2, md: 2 }} sx={{ }} >
                        <Typography component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                            <FontAwesomeIcon icon={faCalendarAlt} style={{ paddingLeft: '0px', paddingRight: '10px' }} />
                            {t('field.createddate')}
                        </Typography>
                    </Grid>
                    <Grid item size={{ xs: 5, md: 8 }} sx={{ paddingRight: '20px'}} >
                        <Typography component="div" sx={{ flexGrow: 1 }} color="primary">
                            {utils.date.dateToString(postValue?.CreatedDate, "YYYY-MM-DD HH:mm")}
                        </Typography>
                    </Grid>
                    <Grid item size={{ xs: 2, md: 1 }} sx={{ }} >
                        <Typography component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                            <FontAwesomeIcon icon={faBookOpen} style={{ paddingLeft: '0px', paddingRight: '10px' }} />
                            {t('field.boardhit')}
                        </Typography>
                    </Grid>
                    <Grid item size={{ xs: 3, md: 1 }} sx={{ }} >
                        <Typography component="div" sx={{ flexGrow: 1, textAlign: "right", paddingRight: "20px" }} color="primary">
                            {postValue.BoardHit.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        </Typography>
                    </Grid>

                    <Grid item className="divline" size={{ xs: 12, sm: 12, md: 12,  }} />
                    <Grid item size={{ xs: 12, sm: 2, md: 2 }} sx={{ }} >
                        <Typography component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                            <FontAwesomeIcon icon={faNewspaper} style={{ paddingLeft: '0px', paddingRight: '10px' }} />
                            <Trans i18nKey='field.content' />
                        </Typography>
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 10, md: 10 }} sx={{ paddingRight: '10px'}} >
                        <div style={{whiteSpace: "pre-wrap", padding: '10px'}}>
                            {postValue.Content.split('\n').map((item, idx) => {
                                return (
                                    <Typography key={`${idx}_${item}`}>&nbsp; {item}</Typography>
                                )})
                            }
                        </div>
                    </Grid>

                    {(boardInfo?.BoardType === 'qna' && postValue?.ReplyBody) ?
                    <>
                    <Grid item className="divdarkline" size={{ xs: 12, sm: 12, md: 12,  }} />
                    <Grid item size={{ xs: 12, sm: 2, md: 2 }} sx={{ }} >
                        <Typography component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                            <FontAwesomeIcon icon={faNewspaper} style={{ paddingLeft: '0px', paddingRight: '10px' }} />
                            <Trans i18nKey='field.replybody' />
                        </Typography>
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 10, md: 10 }} sx={{ paddingRight: '10px'}} >
                        <div style={{whiteSpace: "pre-wrap", padding: '10px'}}>
                            {postValue.ReplyBody.split('\n').map((item, idx) => {
                                return (
                                    <Typography key={`${idx}_${item}`}>&nbsp; {item}</Typography>
                                )
                            })
                            }
                        </div>
                    </Grid>
                    </>: null}
                </Grid>
            </Container>

            {/* Comment */}
            {(boardInfo?.CommentYn === 'Y' ) ?
            <>
            {/* <Divider sx={{ my: 1, borderStyle: "dotted" }} /> */}
            <Container sx={{ p: 1, backgroundColor: 'background.paper', borderRadius: 1, border: '1px solid #ccc' }}>
                <ControlComment id="commentList" boardId={postValue.BoardId} refMessage={refSnackbar.current} />
            </Container></> : null
            }

            {/* 알림 메시지 영역 */}
            <Dialog4Alert ref={refAlert} />
            <Dialog4Confirm ref={refConfirm} />
            <ControlSnackbar ref={refSnackbar} />
            <ControlCircularProgressBar ref={showTimerControler} initialTime={1} />
            <Typography>&nbsp;</Typography>
            
            {/* 게시글 작성 화면 */}
            <ControlWriteArticle id="writeArticle" open={openEditDialog.isOpen} closeEditDialog={closeEditDialog} manageId={process.env.REACT_APP_BOARD_QNA_MANAGE_ID} boardId={openEditDialog.boardId} refMessage={refSnackbar.current} />
    
        </ContentLayout>
    );
}